import React from "react";
import { Modal } from "react-bootstrap";
import Resources from "../../../types/Resources";
import ChecklistRadioOptions from "../../Common/ChecklistRadioOptions";
import WorkAtHeightChecklistFormState from "../../../types/forms/Permits/Conditions/WorkAtHeightChecklistFormState";
import TextArea from "../../Common/TextArea";
import { WORK_AT_HEIGHT_CHECKLIST_VERSION_2026_02 } from "../../../types/permits/conditions/WorkAtHeightChecklist";

type WorkAtHeightChecklistModalProps = {
    checklistVersion: number;
    resources: Resources;
    formState: WorkAtHeightChecklistFormState;
    onChange: (fn: (prevValue: WorkAtHeightChecklistFormState) => WorkAtHeightChecklistFormState) => void;
    show: boolean;
    onHide: () => void;
    canManageContent: boolean;
};

function WorkAtHeightChecklistModal({
    resources,
    formState,
    onChange,
    show,
    onHide,
    canManageContent,
    checklistVersion
}: WorkAtHeightChecklistModalProps) {
    function changeHandler(key: keyof WorkAtHeightChecklistFormState, value: any) {
        onChange((prevValue) => {
            const updatedFormState: WorkAtHeightChecklistFormState = {
                ...prevValue,
                [key]: {
                    ...prevValue[key],
                    value
                }
            };

            return updatedFormState;
        });
    }

    return (
        <Modal show={show} onHide={onHide} size="xl">
            <Modal.Header closeButton>
                <h1 className="h5">{resources.get("ChecklistHeight")}</h1>
            </Modal.Header>
            <Modal.Body>
                {checklistVersion === WORK_AT_HEIGHT_CHECKLIST_VERSION_2026_02 ? (
                    <ChecklistVersion202602
                        canManageContent={canManageContent}
                        changeHandler={changeHandler}
                        formState={formState}
                        resources={resources}
                    />
                ) : (
                    <ChecklistVersion0
                        canManageContent={canManageContent}
                        changeHandler={changeHandler}
                        formState={formState}
                        resources={resources}
                    />
                )}
            </Modal.Body>
        </Modal>
    );
}

type ChecklistProps = {
    resources: Resources;
    formState: WorkAtHeightChecklistFormState;
    canManageContent: boolean;
    changeHandler: (key: keyof WorkAtHeightChecklistFormState, value: any) => void;
};

function ChecklistVersion0({ resources, formState, changeHandler, canManageContent }: ChecklistProps) {
    return (
        <ul className="list-unstyled">
            <li className="m-3">
                <ChecklistRadioOptions
                    id="have-correct-fall-protection"
                    labelText={resources.get("HaveCorrectFallProtection")}
                    onChange={(updatedValue) => changeHandler("haveCorrectFallProtection", updatedValue)}
                    resources={resources}
                    value={formState.haveCorrectFallProtection.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="is-harness-inspected"
                    labelText={resources.get("IsHarnessInspected")}
                    onChange={(updatedValue) => changeHandler("isHarnessInspected", updatedValue)}
                    resources={resources}
                    value={formState.isHarnessInspected.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="is-connected-properly"
                    labelText={resources.get("IsConnectedProperly")}
                    onChange={(updatedValue) => changeHandler("isConnectedProperly", updatedValue)}
                    resources={resources}
                    value={formState.isConnectedProperly.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="is-risk-of-falling-objects"
                    labelText={resources.get("IsRiskOfFallingObjects")}
                    onChange={(updatedValue) => changeHandler("isRiskOfFallingObjects", updatedValue)}
                    resources={resources}
                    value={formState.isRiskOfFallingObjects.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="are-escape-routes-cleared"
                    labelText={resources.get("AreEscapeRoutesCleared")}
                    onChange={(updatedValue) => changeHandler("areEscapeRoutesCleared", updatedValue)}
                    resources={resources}
                    value={formState.areEscapeRoutesCleared.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="is-risk-of-debris"
                    labelText={resources.get("IsRiskOfDebris")}
                    onChange={(updatedValue) => changeHandler("isRiskOfDebris", updatedValue)}
                    resources={resources}
                    value={formState.isRiskOfDebris.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="is-area-cordoned-off"
                    labelText={resources.get("IsAreaCordonedOff")}
                    onChange={(updatedValue) => changeHandler("isAreaCordonedOff", updatedValue)}
                    resources={resources}
                    value={formState.isAreaCordonedOff.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="needs-protective-net"
                    labelText={resources.get("NeedsProtectiveNet")}
                    onChange={(updatedValue) => changeHandler("needsProtectiveNet", updatedValue)}
                    resources={resources}
                    value={formState.needsProtectiveNet.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="will-be-loose-objects"
                    labelText={resources.get("WillBeLooseObjects")}
                    onChange={(updatedValue) => changeHandler("willBeLooseObjects", updatedValue)}
                    resources={resources}
                    value={formState.willBeLooseObjects.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <label htmlFor="will-be-loose-objects-comment">{resources.get("WillBeLooseObjectsComment")}</label>
                <textarea
                    className={`form-control ${!canManageContent ? "read-only-input" : ""}`}
                    id="will-be-loose-objects-comment"
                    rows={3}
                    value={formState.willBeLooseObjectsComment.value ?? ""}
                    onChange={(e) => changeHandler("willBeLooseObjectsComment", e.target.value)}
                    readOnly={!canManageContent}
                ></textarea>
            </li>
        </ul>
    );
}

function ChecklistVersion202602({ resources, formState, changeHandler, canManageContent }: ChecklistProps) {
    return (
        <ul className="list-unstyled">
            <li className="m-3">
                <ChecklistRadioOptions
                    id="have-correct-fall-protection"
                    labelText={resources.get("HaveCorrectFallProtection")}
                    onChange={(updatedValue) => changeHandler("haveCorrectFallProtection", updatedValue)}
                    resources={resources}
                    value={formState.haveCorrectFallProtection.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="is-harness-inspected-1"
                    labelText={resources.get("IsHarnessInspectedWithinOneYear")}
                    onChange={(updatedValue) => changeHandler("isHarnessInspectedWithinOneYear", updatedValue)}
                    resources={resources}
                    value={formState.isHarnessInspectedWithinOneYear.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="is-connected-properly-1"
                    labelText={resources.get("IsSecurelyConnectedToFallProtection")}
                    onChange={(updatedValue) => changeHandler("isSecurelyConnectedToFallProtection", updatedValue)}
                    resources={resources}
                    value={formState.isSecurelyConnectedToFallProtection.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="needs-protective-net"
                    labelText={resources.get("NeedsProtectiveNet")}
                    onChange={(updatedValue) => changeHandler("needsProtectiveNet", updatedValue)}
                    resources={resources}
                    value={formState.needsProtectiveNet.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="is-risk-of-falling-objects"
                    labelText={resources.get("IsRiskOfFallingObjects")}
                    onChange={(updatedValue) => changeHandler("isRiskOfFallingObjects", updatedValue)}
                    resources={resources}
                    value={formState.isRiskOfFallingObjects.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <ChecklistRadioOptions
                    id="is-risk-of-debris"
                    labelText={resources.get("IsRiskOfDebris")}
                    onChange={(updatedValue) => changeHandler("isRiskOfDebris", updatedValue)}
                    resources={resources}
                    value={formState.isRiskOfDebris.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-3">
                <TextArea
                    textAreaId="is-area-cordoned-off-description-1"
                    value={formState.howIsAreaBelowCordonedOff.value}
                    onChange={(s) => changeHandler("howIsAreaBelowCordonedOff", s)}
                    readOnly={!canManageContent}
                    errorMessage={formState.howIsAreaBelowCordonedOff.errorMessage}
                    labelText={resources.get("HowIsAreaBelowCordonedOff")}
                />
            </li>
            <li className="m-3">
                <div className={"form-group"}>
                    <label className={`form-label fw-bold`} htmlFor="will-be-loose-objects-comment-1">
                        <div>{resources.get("HowAreLooseObjectsSecured")}</div>
                        <div className="text-muted">{resources.get("HowAreLooseObjectsSecuredSubText")}</div>
                    </label>
                    <textarea
                        id="will-be-loose-objects-comment-1"
                        className={`form-control ${!canManageContent ? "read-only-input" : ""}`}
                        rows={3}
                        value={formState.howAreLooseObjectsSecured.value}
                        onChange={(e) => changeHandler("howAreLooseObjectsSecured", e.target.value)}
                        readOnly={!canManageContent}
                    ></textarea>
                </div>
            </li>
        </ul>
    );
}

export default WorkAtHeightChecklistModal;
